import { Link } from "react-router-dom";
import { Globe } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { translations, LANGUAGES, LANGUAGE_LABELS } from "@/i18n/translations";
import type { Language } from "@/i18n/translations";
import logo from "@/assets/hadassah-logo.png";
import GoogleTranslate from "@/components/GoogleTranslate";
import { useSiteSettings } from "@/hooks/useSiteSettings";

const FALLBACK_EMAIL = "Info@hadassahhomesinc.com";
const FALLBACK_PHONE_DISPLAY = "(781) 632-3936";
const FALLBACK_INSTAGRAM = "https://www.instagram.com/hadassahhomes/";
const FALLBACK_LINKEDIN = "https://www.linkedin.com/company/hadassahhomes/";

const toTel = (phone: string) => `tel:${phone.replace(/[^+\d]/g, "")}`;

const Footer = () => {
  const { lang, setLang, t } = useLanguage();
  const { getSetting } = useSiteSettings();

  const email = getSetting("contact.email", FALLBACK_EMAIL);
  const phoneDisplay = getSetting("contact.phone", FALLBACK_PHONE_DISPLAY);
  const instagramUrl = getSetting("social.instagram_url", FALLBACK_INSTAGRAM);
  const linkedinUrl = getSetting("social.linkedin_url", FALLBACK_LINKEDIN);
  const facebookUrl = getSetting("social.facebook_url", "");
  const youtubeUrl = getSetting("social.youtube_url", "");
  const tiktokUrl = getSetting("social.tiktok_url", "");
  const footerDescriptionOverride = getSetting("footer.description", "");
  const copyrightOverride = getSetting("footer.copyright_text", "");

  const navLinks = [
    { label: "Home", href: "/" },
    { label: t(translations.nav.about), href: "/about" },
    { label: "Team", href: "/team" },
    { label: "Events", href: "/events" },
    { label: "Listings", href: "/listings" },
    { label: "Dominican Republic Investing", href: "/dominican-republic" },
    { label: "Book a Consultation", href: "/consultation" },
  ];

  const serviceLinks = [
    { label: "Buy a Home", href: "/buy-a-home" },
    { label: "Sell Your Home", href: "/sell-your-home" },
    { label: "Invest in Real Estate", href: "/invest" },
    { label: "Interior Design", href: "/interior-design" },
    { label: "Cleaning", href: "/cleaning" },
    { label: "Carpentry", href: "/carpentry" },
    { label: "Painting", href: "/painting" },
  ];

  const resourceLinks = [
    { label: "Guides", href: "/resources/guides" },
    { label: "FAQ", href: "/faq" },
  ];

  return (
    <footer className="bg-primary py-20 md:py-28" role="contentinfo">
      <div className="mx-auto max-w-wide px-6 md:px-12">
        <div className="flex flex-col md:flex-row justify-between items-start gap-10 md:gap-16">
          <div className="max-w-sm">
            <img
              src={logo}
              alt="Hadassah Homes"
              className="h-12 md:h-16 w-auto mb-5 object-contain"
              loading="lazy"
              width={192}
              height={64}
            />
            <p className="text-primary-foreground/50 text-left leading-relaxed text-base">
              {footerDescriptionOverride || t(translations.footer.description)}
            </p>
            <div className="mt-5 flex flex-col gap-2 text-base">
              <a href={`mailto:${email}`} className="text-primary-foreground/50 hover:text-accent transition-colors">
                {email}
              </a>
              <a href={toTel(phoneDisplay)} className="text-primary-foreground/50 hover:text-accent transition-colors">
                {phoneDisplay}
              </a>
            </div>
          </div>

          <div className="flex gap-12 md:gap-16 flex-wrap">
            <div>
              <h4 className="font-body text-sm uppercase tracking-widest text-accent mb-5">
                {t(translations.footer.navigate)}
              </h4>
              <div className="flex flex-col gap-3">
                {navLinks.map((link) => (
                  <Link key={link.href} to={link.href} className="inline-flex min-h-11 items-center text-primary-foreground/50 hover:text-accent transition-colors text-[15px]">
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
            <div>
              <h4 className="font-body text-sm uppercase tracking-widest text-accent mb-5">
                {t(translations.nav.services)}
              </h4>
              <div className="flex flex-col gap-3">
                {serviceLinks.map((link) => (
                  <Link key={link.href} to={link.href} className="inline-flex min-h-11 items-center text-primary-foreground/50 hover:text-accent transition-colors text-[15px]">
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
            <div>
              <h4 className="font-body text-sm uppercase tracking-widest text-accent mb-5">
                Resources
              </h4>
              <div className="flex flex-col gap-3">
                {resourceLinks.map((link) => (
                  <Link key={link.href} to={link.href} className="inline-flex min-h-11 items-center text-primary-foreground/50 hover:text-accent transition-colors text-[15px]">
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
            <div>
              <h4 className="font-body text-sm uppercase tracking-widest text-accent mb-5">
                {t(translations.footer.connect)}
              </h4>
              <div className="flex flex-col gap-3">
                <a href={instagramUrl} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-11 items-center text-primary-foreground/50 hover:text-accent transition-colors text-[15px]">
                  Instagram
                </a>
                <a href={linkedinUrl} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-11 items-center text-primary-foreground/50 hover:text-accent transition-colors text-[15px]">
                  LinkedIn
                </a>
                {facebookUrl ? (
                  <a href={facebookUrl} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-11 items-center text-primary-foreground/50 hover:text-accent transition-colors text-[15px]">
                    Facebook
                  </a>
                ) : null}
                {youtubeUrl ? (
                  <a href={youtubeUrl} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-11 items-center text-primary-foreground/50 hover:text-accent transition-colors text-[15px]">
                    YouTube
                  </a>
                ) : null}
                {tiktokUrl ? (
                  <a href={tiktokUrl} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-11 items-center text-primary-foreground/50 hover:text-accent transition-colors text-[15px]">
                    TikTok
                  </a>
                ) : null}
                <a href={`mailto:${email}`} className="inline-flex min-h-11 items-center text-primary-foreground/50 hover:text-accent transition-colors text-[15px]">
                Email
              </a>
              <div className="mt-3 pt-3 border-t border-primary-foreground/10">
                <GoogleTranslate variant="dark" />
              </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 md:mt-16 pt-6 border-t border-primary-foreground/10 space-y-5">
          <p className="text-primary-foreground/40 text-xs text-center leading-relaxed">
            {t(translations.footer.affiliation)}
          </p>
          <div className="flex flex-wrap justify-center gap-4 text-primary-foreground/35 text-xs">
            <Link to="/privacy-policy" className="hover:text-accent transition-colors">Privacy Policy</Link>
            <span className="text-primary-foreground/20">·</span>
            <Link to="/terms-of-service" className="hover:text-accent transition-colors">Terms of Service</Link>
            <span className="text-primary-foreground/20">·</span>
            <Link to="/sitemap" className="hover:text-accent transition-colors">Sitemap</Link>
          </div>
          <div className="flex flex-col md:flex-row justify-between items-center gap-2">
            <p className="text-primary-foreground/30 text-xs">
              {copyrightOverride || `© ${new Date().getFullYear()} Hadassah Homes. ${t(translations.footer.rights)}`}
            </p>
            <p className="text-primary-foreground/30 text-xs font-headline italic tracking-wider">
              {t(translations.footer.tagline)}
            </p>
          </div>
          <p className="text-primary-foreground/20 text-[11px] text-center">
            Website by{" "}
            <a href="http://evanderpierre.com" target="_blank" rel="noopener noreferrer" className="hover:text-accent transition-colors">
              evanderpierre.com
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
